import React, { Suspense } from 'react';
import PropTypes from 'prop-types';
import {
  BrowserRouter, Switch, Route, MemoryRouter, useLocation,
} from 'react-router-dom';
import PrivateRoute from './PrivateRoute';
import {
  ROUTE_TYPE_PRIVATE, ROUTE_TYPE_PUBLIC, ROUTE_TYPE_ANTI_PRIVATE,
} from './route-constants';
import { appRoutesPropTypes, flattenAppRoutesPropTypes } from './route-prop-types';
import BasePage from '../page/BasePage';
import AntiPrivateRoute from './AntiPrivateRoute';
import { AppState } from '../state/MyStateProvider';
import defaultAppConfigPropTypes from '../app/default-app-prop-types';
import { getFlattenRoutes, getRouteDialogs } from './route-helpers';
import { FirebaseState } from '../firebase/MyFirebaseProvider';

const FlatRouteProvider = ({
  flatAppRoutes,
  dialogRoutes,
  notFoundPage,
  loadingPage,
  AuthenticationRepository,
  applicationName,
  commonComponentProps,
  icons,
}) => {
  const { state, dispatch } = React.useContext(AppState);
  const { firebaseConfig } = React.useContext(FirebaseState);
  const authRepo = new AuthenticationRepository(state, dispatch, firebaseConfig);
  const isAuthenticated = authRepo.isAuthenticated();

  const location = useLocation();
  const background = location.state && location.state.background;

  // const flatRoutes = getFlattenRoutes(routes);
  // console.log(appRoutes);
  return (
    <Suspense fallback={loadingPage}>
      <Switch location={background || location}>
        {
        flatAppRoutes.map((route) => {
          switch (route.type) {
            case ROUTE_TYPE_PRIVATE:
              return (
                <PrivateRoute
                  key={`route-${route.path}`}
                  exact={route.exact}
                  path={route.path}
                  isAuthenticated={isAuthenticated}
                  redirectPath="/login"
                >
                  <BasePage
                    titleId={route.titleId}
                    Component={route.Component}
                    AuthenticationRepository={AuthenticationRepository}
                    componentProps={route.componentProps}
                    applicationName={applicationName}
                    commonComponentProps={commonComponentProps}
                    icons={icons}
                  />
                </PrivateRoute>
              );
            case ROUTE_TYPE_PUBLIC:
              return (
                <Route
                  key={`route-${route.path}`}
                  exact={route.exact}
                  path={route.path}
                >
                  <BasePage
                    titleId={route.titleId}
                    Component={route.Component}
                    AuthenticationRepository={AuthenticationRepository}
                    componentProps={route.componentProps}
                    applicationName={applicationName}
                    commonComponentProps={commonComponentProps}
                    icons={icons}
                  />
                </Route>
              );
            case ROUTE_TYPE_ANTI_PRIVATE:
              return (
                <AntiPrivateRoute
                  key={`route-${route.path}`}
                  exact={route.exact}
                  path={route.path}
                  isAuthenticated={isAuthenticated}
                  redirectPath="/"
                >
                  <BasePage
                    titleId={route.titleId}
                    Component={route.Component}
                    AuthenticationRepository={AuthenticationRepository}
                    componentProps={route.componentProps}
                    applicationName={applicationName}
                    commonComponentProps={commonComponentProps}
                    icons={icons}
                  />
                </AntiPrivateRoute>
              );
            default:
              return null;
          }
        })
      }
        <Route path="*">
          {notFoundPage}
        </Route>
      </Switch>
      {
        dialogRoutes.map((route) => (
          background
            && (
            <Route
              key={`route-dialog-${route.path}`}
              path={route.path}
              render={() => (
                <BasePage
                  titleId={route.titleId}
                  Component={route.ComponentDialog}
                  AuthenticationRepository={AuthenticationRepository}
                  componentProps={route.componentProps}
                  applicationName={applicationName}
                  commonComponentProps={commonComponentProps}
                  icons={icons}
                />
              )}
            />
            )
        ))
      }
    </Suspense>
  );
};

FlatRouteProvider.propTypes = {
  flatAppRoutes: PropTypes.arrayOf(flattenAppRoutesPropTypes).isRequired,
  notFoundPage: PropTypes.node.isRequired,
  loadingPage: PropTypes.node.isRequired,
  AuthenticationRepository: PropTypes.func.isRequired,
  applicationName: PropTypes.string.isRequired,
  commonComponentProps: PropTypes.objectOf(PropTypes.any).isRequired,
  icons: PropTypes.objectOf(PropTypes.object).isRequired,
};

const MyRouteProvider = ({
  appRoutes,
  notFoundPage,
  loadingPage,
  AuthenticationRepository,
  applicationName,
  commonComponentProps,
  icons,
  initialRouteEntries,
}) => {
  const flatAppRoutes = getFlattenRoutes(appRoutes);
  const dialogRoutes = getRouteDialogs(appRoutes);

  let Router;
  if (initialRouteEntries) {
    Router = MemoryRouter;
  } else {
    Router = BrowserRouter;
  }

  return (
    <Router initialEntries={initialRouteEntries}>
      <FlatRouteProvider
        flatAppRoutes={flatAppRoutes}
        dialogRoutes={dialogRoutes}
        notFoundPage={notFoundPage}
        loadingPage={loadingPage}
        AuthenticationRepository={AuthenticationRepository}
        applicationName={applicationName}
        commonComponentProps={commonComponentProps}
        icons={icons}
      />
    </Router>
  );
};

MyRouteProvider.propTypes = {
  appRoutes: PropTypes.arrayOf(appRoutesPropTypes),
  notFoundPage: PropTypes.node,
  loadingPage: PropTypes.node,
  AuthenticationRepository: PropTypes.func,
  applicationName: PropTypes.string,
  commonComponentProps: PropTypes.objectOf(PropTypes.any),
  icons: PropTypes.objectOf(PropTypes.object),
};

MyRouteProvider.defaultProps = defaultAppConfigPropTypes;

export default MyRouteProvider;
